import hljs from 'highlight.js/lib/core';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import {
  Button,
  Dropdown,
  DropdownOption,
  Icon,
  Modal,
  ModalSize,
  SegmentedControl,
  SegmentedControlVariant,
} from '@column/column-ui-kit';

import { NotificationList } from '~/components/NotificationList';
import { EventType, WebhookRepository, WebhookVerify } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Grid } from '~/styles';

const Check = styled(Icon.CircleCheck)`
  --icon-color: ${({ theme }) => theme.success.background};
`;

const Cross = styled(Icon.CircleCross)`
  --icon-color: ${({ theme }) => theme.danger.background};
`;

const Status = styled.div`
  display: flex;
  gap: 8px;
`;

const StatusLabel = styled.div`
  line-height: 20px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.secondary.background};
`;

const Code = styled.div`
  padding: 24px 32px;
  border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};
  background-color: ${({ theme }) => theme.secondary.blendToBackground(25)};
  border-radius: 0 0 24px 24px;
  margin-top: -1px;
  overflow-y: auto;
  font-size: 13px;
  line-height: 1.6;
  max-height: min(560px, calc(100dvh - 300px));
  height: 560px;

  pre {
    margin: 0;
    padding: 0;

    code.hljs {
      background: none;
      padding: 0;
      font-family: 'Roboto Mono', monospace;
    }
  }
`;

export interface WebhookResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  webhookId: string;
  webhookResult?: WebhookVerify;
  testableEvents: DropdownOption[];
  testEventType?: string;
}

export const WebhookResultModal: FC<WebhookResultModalProps> = ({
  isOpen,
  onClose,
  webhookId,
  webhookResult: initialWebhookResult,
  testableEvents,
  testEventType: initialTestEventType,
}) => {
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const currentUser = useSessionStore((state) => state.currentUser);

  const [testEventType, setTestEventType] = useState<string>(initialTestEventType || '');
  const [webhookResult, setWebhookResult] = useState<WebhookVerify | undefined>(initialWebhookResult);
  const [activeTab, setActiveTab] = useState<string>('request');
  const codeRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTestEventType(initialTestEventType || '');
      setWebhookResult(initialWebhookResult);
      setActiveTab('request');
    }
  }, [isOpen, initialTestEventType, initialWebhookResult]);

  useEffect(() => {
    if (codeRef.current) {
      // Remove previous highlighting
      codeRef.current.removeAttribute('data-highlighted');
      hljs.highlightElement(codeRef.current);
    }
  }, [webhookResult?.requestBody, webhookResult?.responseBody, activeTab]);

  const handleTestWebhook = useCallback(() => {
    if (!currentUser || !webhookId) {
      return;
    }
    WebhookRepository.verify(webhookId, {
      eventType: testEventType as EventType,
    })
      .then((response) => {
        setWebhookResult(response);
        if (response.success) {
          addSuccessNotification({
            content: `${testEventType} successfully sent`,
            display: 'popup',
          });
          return;
        }
        addDangerNotification({
          content: `${response.responseCode} Error`,
          display: 'popup',
        });
      })
      .catch((error) => {
        addDangerNotification({
          content: error.message,
          display: 'popup',
        });
      });
  }, [currentUser, webhookId, testEventType, addSuccessNotification, addDangerNotification]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={ModalSize.Large} withClose={false}>
      <Modal.Header title="Verify Webhook">
        <Modal.Close variant="subtle" size="small" onClick={onClose} />
      </Modal.Header>
      <Modal.Content>
        <NotificationList display="popup" spacing="top" variant="light" />
        <Grid gap={12} vertical>
          <Flex alignItems="center" flexDirection="row" gap={12}>
            <Dropdown
              search
              active={testEventType}
              placeholder="Select event type"
              variant="secondary"
              options={testableEvents}
              onChange={(value: any) => setTestEventType(value)}
              size="small"
            />
            <Button onClick={handleTestWebhook} variant="secondary" icon={<Icon.Bell />}>
              Send
            </Button>
            <Status>
              {typeof webhookResult?.responseCode !== 'undefined' &&
              webhookResult.responseCode >= 200 &&
              webhookResult.responseCode < 300 ? (
                <Check />
              ) : (
                <Cross />
              )}
              <StatusLabel>
                {webhookResult?.responseCode}{' '}
                {typeof webhookResult?.responseCode !== 'undefined' &&
                webhookResult.responseCode >= 200 &&
                webhookResult.responseCode < 300
                  ? 'OK'
                  : 'Error'}
              </StatusLabel>
            </Status>
          </Flex>
          <SegmentedControl
            options={[
              { label: 'Request', value: 'request' },
              { label: 'Response', value: 'response' },
            ]}
            onOptionChange={setActiveTab}
            active={activeTab}
            variant={SegmentedControlVariant.Line}
          />
        </Grid>
      </Modal.Content>
      <Code>
        <pre>
          <code ref={codeRef} className="language-json">
            {(activeTab === 'request' ? webhookResult?.requestBody : webhookResult?.responseBody) ?? ''}
          </code>
        </pre>
      </Code>
    </Modal>
  );
};
